import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../lib/auth.js';

/**
 * Top nav — trimmed to what a landlord touches daily.
 * The rest lives behind "More" so the header stays calm.
 */

const primary = [
  { to: '/',         label: 'Dashboard' },
  { to: '/rent',     label: 'Rent' },
  { to: '/tenants',  label: 'Tenants' },
  { to: '/expenses', label: 'Expenses' },
];

const more = [
  { to: '/properties',   label: 'Properties' },
  { to: '/income',       label: 'Other income' },
  { to: '/electricity',  label: 'Electricity' },
  { to: '/reports',      label: 'Reports' },
  { to: '/invitations',  label: 'Invitations' },
];
// Assets & Notifications intentionally removed from the visible nav —
// they still work by URL if needed, but they're not part of the daily flow.

export function Nav() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <nav className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-x-4 gap-y-2 px-4 py-3">
        <div className="flex items-center gap-5">
          <strong className="text-base">Rental &amp; Finance</strong>
          {primary.map((p) => (
            <NavLink key={p.to} to={p.to} end={p.to === '/'} className={linkCls}>
              {p.label}
            </NavLink>
          ))}
          <div className="relative" ref={ref}>
            <button
              type="button"
              className="text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              onClick={() => setOpen((v) => !v)}
              aria-haspopup="menu" aria-expanded={open}
            >
              More ▾
            </button>
            {open && (
              <div
                role="menu"
                className="absolute left-0 top-full z-10 mt-1 w-48 rounded border border-slate-200 bg-white py-1 shadow-md dark:border-slate-700 dark:bg-slate-800"
              >
                {more.map((m) => (
                  <NavLink
                    key={m.to} to={m.to} onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block px-3 py-1.5 text-sm ${isActive ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'} hover:bg-slate-100 dark:hover:bg-slate-700`
                    }
                  >
                    {m.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-slate-500 md:inline">{user?.email}</span>
          <button onClick={() => void logout()} className="text-xs underline decoration-dotted">
            Sign out
          </button>
        </div>
      </div>
    </nav>
  );
}

const linkCls = ({ isActive }: { isActive: boolean }) =>
  `text-sm ${isActive ? 'font-medium text-slate-900 dark:text-slate-100' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'}`;
